<script setup>
import { ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useAttachmentLoadState } from 'dashboard/composables/useAttachmentLoadState';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});
const hasLoadError = ref(false);
const isLoaded = ref(false);
const showGallery = ref(false);

const { filteredCurrentChatAttachments, createdAt } = useMessageContext();
const { hasFailed, markLoadFailure, reset } = useAttachmentLoadState(createdAt);

const handleError = () => {
  hasLoadError.value = true;
  isLoaded.value = false;
  markLoadFailure();
};

// The file may not be in the storage yet when the message is first received. message.updated is
// sent once the file is uploaded: show the <img> again so the browser requests it again.
watch(
  () => props.attachment,
  () => {
    if (!hasLoadError.value) return;

    reset();
    hasLoadError.value = false;
  }
);
</script>

<template>
  <div
    class="relative w-72 max-w-full overflow-hidden contain-content rounded-xl cursor-pointer"
    :class="{ 'min-h-40': !isLoaded }"
    @click="showGallery = true"
  >
    <div
      v-if="hasFailed"
      class="flex flex-col items-center justify-center gap-1 h-40 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <template v-else>
      <img
        v-if="!hasLoadError"
        class="object-cover w-full h-auto max-h-96 skip-context-menu"
        :class="{ 'opacity-0': !isLoaded }"
        :src="attachment.dataUrl"
        @load="isLoaded = true"
        @error="handleError"
      />
      <div
        v-if="!isLoaded"
        data-testid="media-loading"
        class="absolute inset-0 flex items-center justify-center bg-n-alpha-1"
        :title="$t('COMPONENTS.MEDIA.LOADING')"
      >
        <Icon
          icon="i-lucide-loader-circle"
          class="size-5 text-n-slate-11 animate-spin"
        />
      </div>
    </template>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
